<template>
  <div class="flex mr-4" role="tablist" aria-orientation="horizontal">
    <div v-for="(tab, tabIndex) in tabs" :key="tab.title || tabIndex" class="tab-container">
      <button
        v-if="!showSelectForTab[tabIndex]"
        :key="tab.title"
        class="p-tab"
        :class="{ 'p-tab-active': activeTabIndex === tabIndex }"
        @click="() => onTabClick(tabIndex)"
        @dblclick="() => onTabDoubleClick(tabIndex, tab)"
      >
        <span>
          {{ getTabLabel(tab, tabIndex) }}
          <span v-if="tab.options.length > 1" class="text-xs count ml-2">
            ({{ (activeOptionIndexByTab[tabIndex] ?? 0) + 1 }}/{{ tab.options.length }})
          </span>
        </span>
      </button>
      <div v-if="showSelectForTab[tabIndex]" class="selectWrapper" @click.stop>
        <Select
          :modelValue="activeOptionIndexByTab[tabIndex]"
          :options="tab.options.map((opt, i) => ({ label: opt.title, value: i }))"
          optionLabel="label"
          optionValue="value"
          class="prefilter-select"
          autoFocus
          @change="(event) => onSelectChange(tabIndex, event.value)"
          @blur="() => (showSelectForTab[tabIndex] = false)"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { nextTick, onMounted, onUnmounted, ref, watch } from 'vue';
import Select from 'primevue/select';
import { useI18n } from 'vue-i18n';

import { useTablePrefilterStore } from '@/stores/tablePrefilter';

const { t } = useI18n();
const prefilterStore = useTablePrefilterStore();

const MAX_CYCLE_OPTIONS = 1;

export interface PaginatedPrefilterOption {
  title: string;
  key: string;
  focusedColumn?: string;
}

export interface PaginatedPrefilterTab {
  title: string;
  options: PaginatedPrefilterOption[];
}

interface DataTableFilterProps {
  tabs: PaginatedPrefilterTab[];
  dataKey: string;
}

const props = defineProps<DataTableFilterProps>();
const emit = defineEmits<{
  (e: 'change', payload: PaginatedPrefilterOption): void;
}>();

const activeTabIndex = ref(0);
const activeOptionIndexByTab = ref<number[]>([]);
const showSelectForTab = ref<Record<number, boolean>>({});

function initializeActiveStates() {
  if (!props.tabs || props.tabs.length === 0) return;

  const savedTabIndex = prefilterStore.getSelectedTab(props.dataKey);
  const savedOptionIndices = prefilterStore.getOptionIndices(props.dataKey);

  // Initialize option indices - use saved values or default to 0
  activeOptionIndexByTab.value = props.tabs.map((tab, i) => {
    const savedOptionIndex = savedOptionIndices[i];
    if (savedOptionIndex !== undefined && savedOptionIndex < tab.options.length) {
      return savedOptionIndex;
    }
    return 0;
  });

  // Determine initial tab - use saved value if valid, otherwise default to 0
  if (savedTabIndex !== undefined && savedTabIndex >= 0 && savedTabIndex < props.tabs.length) {
    activeTabIndex.value = savedTabIndex;
  } else {
    activeTabIndex.value = 0;
  }

  void nextTick(() => {
    emitActiveOption();
  });
}

function getTabLabel(tab: PaginatedPrefilterTab, tabIndex: number): string {
  const optionIndex = activeOptionIndexByTab.value[tabIndex] ?? 0;
  const activeOption = tab.options[optionIndex];

  const tabTitle = t(tab.title);
  const optionTitle = activeOption?.title;

  return tab.options.length > 1 ? `${tabTitle}: ${optionTitle}` : tabTitle;
}

function onTabClick(tabIndex: number) {
  // Only activate the clicked tab, don't cycle options
  activeTabIndex.value = tabIndex;

  // Save tab state to store
  prefilterStore.setSelectedTab(props.dataKey, activeTabIndex.value);
  prefilterStore.setOptionIndices(props.dataKey, activeOptionIndexByTab.value);

  emitActiveOption();
}

function onTabDoubleClick(tabIndex: number, tab?: PaginatedPrefilterTab) {
  const currentTab = tab ?? props.tabs[tabIndex];
  if (!currentTab) return;

  // Show dropdown if more than one option
  if (currentTab.options.length > MAX_CYCLE_OPTIONS) {
    showSelectForTab.value = { ...showSelectForTab.value, [tabIndex]: true };
  }
}

function onSelectChange(tabIndex: number, optionIndex: number) {
  activeOptionIndexByTab.value[tabIndex] = optionIndex;
  showSelectForTab.value[tabIndex] = false;

  // Save option indices to store
  prefilterStore.setSelectedTab(props.dataKey, activeTabIndex.value);
  prefilterStore.setOptionIndices(props.dataKey, activeOptionIndexByTab.value);

  emitActiveOption();
}

function emitActiveOption() {
  const tab = props.tabs[activeTabIndex.value];
  if (!tab) return;

  const optIndex = activeOptionIndexByTab.value[activeTabIndex.value] ?? 0;
  const option = tab.options[optIndex];

  if (!option) return;

  emit('change', option);
}

// Close select on outside click
function handleClickOutside(event: MouseEvent) {
  const target = event.target as HTMLElement;
  // Check if click is outside select wrapper
  if (!target.closest('.selectWrapper') && !target.closest('.p-select-overlay')) {
    showSelectForTab.value = {};
  }
}

onMounted(() => {
  initializeActiveStates();
  document.addEventListener('click', handleClickOutside);
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
});

watch(() => props.tabs, initializeActiveStates, { immediate: true });
</script>

<style scoped lang="css">
@layer app {
  .tab-container {
    position: relative;
  }

  .p-tab {
    position: relative;
    padding: 0.6rem 1rem;
    font-weight: 600;
    border: none;
    border-bottom: 3px solid transparent;
    background: none;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .p-tab:hover {
    color: theme('colors.interactionblue.500');
  }

  .p-tab-active {
    border-bottom-color: theme('colors.interactionblue.500');
    color: theme('colors.interactionblue.500');
  }

  .count {
    color: var(--text-secondary);
  }

  .selectWrapper {
    position: absolute;
    overflow: visible;
    z-index: 999;
    bottom: -10px;
    height: auto;
  }

  .prefilter-select {
    position: relative;
    width: 220px;
  }
}
</style>
